import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format, addDays as addDateDays } from "date-fns";
import { ScheduleTypeSelector, type ScheduleType } from "@/components/schedule/ScheduleTypeSelector";
import { ScheduleDatePicker } from "@/components/schedule/ScheduleDatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Upload, FileText, AlertTriangle, CheckCircle2, Import, Lock, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  extractHeader,
  parseFlightTables,
  mergeDayRows,
  validateRows,
  type ScheduleHeader,
  type ValidatedFlightPatternRow,
} from "@/utils/scheduleMarkdownParser";
import { importSchedule, type ImportProgress, type HandledFlightInfo } from "@/services/scheduleImportService";
import { resolveQuestionMarkTimes } from "@/utils/scheduleTimeResolver";
import ScheduleLibrary from "@/components/schedule/ScheduleLibrary";
import CacheStatus from "@/components/schedule/CacheStatus";
import { UnrecognisedRowsModal } from "@/components/schedule/UnrecognisedRowsModal";

type FlightClassification = 'own' | 'handled' | 'unrecognised';

interface ClassifiedRow {
  classification: FlightClassification;
  handledName?: string; // e.g. "Qantas" for handled flights
}

function classifyFlightRow(
  flightNumber: string,
  flightPrefix: string,
  handledCodes: Map<string, string> // code -> name
): ClassifiedRow {
  // Split compound flight number into segments: "PX019/CX6899" → ["PX019", "CX6899"]
  const segments = flightNumber.split('/');

  // Own takes priority — if any segment matches an own prefix, classify as own
  const ownPrefixUpper = flightPrefix?.toUpperCase() ?? '';
  for (const segment of segments) {
    const prefix = segment.trim().match(/^([A-Z]+)/i)?.[1]?.toUpperCase() ?? '';
    if (ownPrefixUpper && prefix === ownPrefixUpper) {
      return { classification: 'own' };
    }
  }

  // No own prefix found — check if any segment is a handled codeshare
  for (const segment of segments) {
    const prefix = segment.trim().match(/^([A-Z]+)/i)?.[1]?.toUpperCase() ?? '';
    for (const [code, name] of handledCodes) {
      if (prefix === code.toUpperCase()) {
        return { classification: 'handled', handledName: name };
      }
    }
  }

  return { classification: 'unrecognised' };
}

interface RemovedRowEntry {
  originalIndex: number;
  row: ValidatedFlightPatternRow;
}

/** Get today's date in PNG time (UTC+10) */
function getPngToday(): Date {
  const now = new Date();
  const pngTime = new Date(now.getTime() + (10 * 60 * 60 * 1000) - (now.getTimezoneOffset() * 60 * 1000));
  return new Date(pngTime.getFullYear(), pngTime.getMonth(), pngTime.getDate());
}

/** Get the PNG day-of-week bit for a given date (Sun=0 bit0, Mon=1 bit1, etc.) */
function getPngDayBit(d: Date): number {
  return d.getDay();
}

/** Compute board status for a row */
function getBoardStatus(
  row: ValidatedFlightPatternRow,
  scheduleType: ScheduleType,
  scheduleDate: Date,
  header: ScheduleHeader | null,
): { isToday: boolean; label: string } {
  const today = getPngToday();
  const todayStr = format(today, 'yyyy-MM-dd');

  if (scheduleType === 'daily') {
    const scheduleDateStr = format(scheduleDate, 'yyyy-MM-dd');
    if (scheduleDateStr === todayStr) {
      return { isToday: true, label: '📺 Today' };
    }
    return { isToday: false, label: format(scheduleDate, 'd MMM') };
  }

  // Seasonal: check if today's day matches bitmask AND today is within validity
  if (header) {
    const todayBit = getPngDayBit(today);
    const matchesToday = (row.days_bitmask & (1 << todayBit)) !== 0;
    const validFrom = new Date(header.valid_from + 'T00:00:00');
    const validTo = new Date(header.valid_to + 'T00:00:00');

    if (matchesToday && today >= validFrom && today <= validTo) {
      return { isToday: true, label: '📺 Today' };
    }

    // Find next operating date
    for (let i = 1; i <= 7; i++) {
      const candidate = addDateDays(today, i);
      const bit = getPngDayBit(candidate);
      if ((row.days_bitmask & (1 << bit)) !== 0 && candidate >= validFrom && candidate <= validTo) {
        const dayName = format(candidate, 'EEE');
        return { isToday: false, label: `Next: ${dayName} ${format(candidate, 'd MMM')}` };
      }
    }
  }

  return { isToday: false, label: '—' };
}

async function generateFileHash(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

interface DuplicateInfo {
  schedule_version: string;
  airline_code: string;
  imported_at: string;
}

export default function ScheduleToolPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [fileHash, setFileHash] = useState<string | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null);
  const [checkingHash, setCheckingHash] = useState(false);
  const [scheduleType, setScheduleType] = useState<ScheduleType>(null);
  const [scheduleDate, setScheduleDate] = useState<Date>(() => {
    // Default to today in PNG time (UTC+10)
    const now = new Date();
    const pngTime = new Date(now.getTime() + (10 * 60 * 60 * 1000) - (now.getTimezoneOffset() * 60 * 1000));
    return new Date(pngTime.getFullYear(), pngTime.getMonth(), pngTime.getDate());
  });
  const [airlineCode, setAirlineCode] = useState("");
  const [scheduleVersion, setScheduleVersion] = useState("");
  const [loading, setLoading] = useState(false);
  const [chunkSize, setChunkSize] = useState(1);
  const [rawMarkdown, setRawMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [header, setHeader] = useState<ScheduleHeader | null>(null);

  // Role-aware state
  const [userRole, setUserRole] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [organizations, setOrganizations] = useState<{ id: string; code: string; name: string }[]>([]);
  const [selectedOrgName, setSelectedOrgName] = useState<string | null>(null);
  const [airlineCodeLocked, setAirlineCodeLocked] = useState(false);
  const [parsedRows, setParsedRows] = useState<ValidatedFlightPatternRow[]>([]);
  const [cityCodes, setCityCodes] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
  const [handledCodes, setHandledCodes] = useState<Map<string, string>>(new Map());
  const [flightPrefix, setFlightPrefix] = useState<string>("");
  const [includedRows, setIncludedRows] = useState<Set<number>>(new Set());
  const [removedRows, setRemovedRows] = useState<RemovedRowEntry[]>([]);
  const [lastRemoved, setLastRemoved] = useState<RemovedRowEntry | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Load city codes on mount for validation
  useEffect(() => {
    const loadCities = async () => {
      const { data, error } = await supabase.from("cities").select("code");
      if (!error && data) {
        setCityCodes(new Set(data.map((c) => c.code.toUpperCase())));
      }
    };
    loadCities();
  }, []);

  // Access control + load user role and organizations on mount
  useEffect(() => {
    const loadUserAndOrgs = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      // Fetch profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("role, organization_id")
        .eq("id", user.id)
        .single();

      if (!profile) {
        navigate('/', { replace: true });
        return;
      }

      const isAdmin = profile.role === 'admin' || profile.role === 'administrator';
      const isOrg = profile.role === 'organization' || profile.role === 'organization_user';

      // Access control: only admin and org roles allowed
      if (!isAdmin && !isOrg) {
        navigate('/', { replace: true });
        return;
      }

      // Fetch active organizations
      const { data: orgs } = await supabase
        .from("organizations")
        .select("id, code, name")
        .eq("is_active", true)
        .order("name");

      const orgList = orgs || [];
      setOrganizations(orgList);

      if (isAdmin) {
        setUserRole('admin');
        // Default to first org if available
        if (orgList.length > 0) {
          setAirlineCode(orgList[0].code);
          setSelectedOrgName(orgList[0].name);
        }
      } else if (isOrg && profile.organization_id) {
        setUserRole('org');
        const userOrg = orgList.find(o => o.id === profile.organization_id);
        if (userOrg) {
          setAirlineCode(userOrg.code);
          setSelectedOrgName(userOrg.name);
          setAirlineCodeLocked(true);
        }
      }

      setAuthChecked(true);
    };
    loadUserAndOrgs();
  }, [navigate]);

  const handleFileChange = async (selectedFile: File | null) => {
    setDuplicateInfo(null);
    setFileHash(null);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setCheckingHash(true);

    try {
      const hash = await generateFileHash(selectedFile);
      setFileHash(hash);

      // Check if hash exists in database
      const { data } = await supabase
        .from("flight_patterns")
        .select("schedule_version, airline_code, imported_at")
        .eq("file_hash", hash)
        .limit(1);

      if (data && data.length > 0) {
        setDuplicateInfo({
          schedule_version: data[0].schedule_version,
          airline_code: data[0].airline_code,
          imported_at: data[0].imported_at || '',
        });
      }
    } catch (err) {
      console.error("Hash generation failed:", err);
    } finally {
      setCheckingHash(false);
    }
  };

  const handleDuplicateCancel = () => {
    setFile(null);
    setFileHash(null);
    setDuplicateInfo(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDuplicateContinue = () => {
    setDuplicateInfo(null);
  };

  const isTimeoutError = (msg: string) =>
    /timed?\s*out|timeout|504|gateway\s*timeout/i.test(msg);

  const handleParse = async () => {
    if (!file) {
      toast({ title: "No file selected", description: "Please select a PDF file.", variant: "destructive" });
      return;
    }
    if (!airlineCode.trim()) {
      toast({ title: "Missing airline code", description: "Please enter an airline code.", variant: "destructive" });
      return;
    }
    if (!scheduleVersion.trim()) {
      toast({ title: "Missing schedule version", description: "Please enter a schedule version.", variant: "destructive" });
      return;
    }

    setLoading(true);
    setRawMarkdown(null);
    setError(null);
    setHeader(null);
    setParsedRows([]);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("airline_code", airlineCode.trim());
      formData.append("schedule_version", scheduleVersion.trim());
      formData.append("chunk_size", String(scheduleType === 'seasonal' ? chunkSize : 10));
      formData.append("schedule_type", scheduleType);

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setError("You must be logged in as an admin to use this tool.");
        setLoading(false);
        return;
      }

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/parse-flight-schedule`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      const json = await res.json();

      if (!res.ok) {
        const serverMsg = json.error || `Error ${res.status}`;
        if (res.status === 504 || isTimeoutError(serverMsg)) {
          setError("Parsing timed out. The PDF may be too large or the service is busy. Please try again.");
        } else {
          setError(serverMsg);
        }
      } else {
        const md = typeof json.markdown === "string" ? json.markdown : JSON.stringify(json.markdown, null, 2);
        setRawMarkdown(md);

        let h = extractHeader(md);

        // For daily operational, override header dates with selected schedule date
        if (scheduleType === 'daily') {
          const dateStr = format(scheduleDate, 'yyyy-MM-dd');
          h = {
            valid_from: dateStr,
            valid_to: dateStr,
            schedule_code: h?.schedule_code || '',
          };
        }

        setHeader(h);

        let parsedRows = parseFlightTables(md);

        // For seasonal imports, run day merge pass to combine per-day rows
        if (scheduleType === 'seasonal') {
          const { merged } = mergeDayRows(parsedRows);
          parsedRows = merged;
        }

        // For daily mode, override days_bitmask to the day of week of scheduleDate
        let validated = validateRows(parsedRows, cityCodes);
        if (scheduleType === 'daily') {
          const dayBit = scheduleDate.getDay(); // 0=Sun
          const bitmask = 1 << dayBit;
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          validated = validated.map(row => ({
            ...row,
            days_bitmask: bitmask,
            days_display: dayNames[dayBit],
          }));
        }

        const { rows: resolved, resolvedCount: rc } = await resolveQuestionMarkTimes(validated);
        setResolvedCount(rc);
        setParsedRows([...resolved]);

        // Fetch handling relationships for this airline code
        // First get the org id for this airline code
        const trimmedCode = airlineCode.trim().toUpperCase();
        const { data: orgData } = await supabase
          .from('organizations')
          .select('id, flight_prefix')
          .eq('code', trimmedCode)
          .eq('is_active', true)
          .single();

        const orgFlightPrefix = orgData?.flight_prefix || trimmedCode;
        setFlightPrefix(orgFlightPrefix);

        const codeMap = new Map<string, string>();
        if (orgData?.id) {
          const { data: handlingData } = await supabase
            .from('organisation_handling_relationships')
            .select('handled_code, handled_name')
            .eq('organisation_id', orgData.id)
            .eq('is_active', true);

          if (handlingData) {
            for (const rel of handlingData) {
              codeMap.set(rel.handled_code.toUpperCase(), rel.handled_name);
            }
          }
        }
        setHandledCodes(codeMap);

        // Set default inclusion: own = checked, handled & unrecognised = unchecked
        const defaultIncluded = new Set<number>();
        resolved.forEach((row, idx) => {
          const cl = classifyFlightRow(row.flight_number, orgFlightPrefix, codeMap);
          if (cl.classification === 'own') {
            defaultIncluded.add(idx);
          }
        });
        setIncludedRows(defaultIncluded);

        toast({ title: "Parse complete", description: `${resolved.length} flight rows extracted${rc > 0 ? `, ${rc} times auto-resolved` : ''}.` });
      }
    } catch (err: unknown) {
      console.error("Parse error:", err);
      const msg = err instanceof Error ? err.message : "Unknown error";
      if (isTimeoutError(msg)) {
        setError("Parsing timed out. The PDF may be too large or the service is busy. Please try again.");
      } else {
        setError(`Parsing failed: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const isValidTime = (t: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);

  const handleTimeChange = (rowIndex: number, field: 'dep_time' | 'arr_time', value: string) => {
    setParsedRows(prev => {
      const updated = [...prev];
      const row = { ...updated[rowIndex] };
      row[field] = value;

      row.warnings = row.warnings.filter(w => !w.includes('dep_time') && !w.includes('arr_time'));

      const depOk = isValidTime(row.dep_time);
      const arrOk = isValidTime(row.arr_time);

      // Only warn if BOTH times are missing/unresolved
      if (!depOk && !arrOk) {
        if (row.dep_time === '?' || row.dep_time === '') {
          row.warnings.push('dep_time unresolved');
        }
        if (row.arr_time === '?' || row.arr_time === '') {
          row.warnings.push('arr_time unresolved');
        }
      }

      if (depOk && arrOk) {
        row.is_overnight = row.arr_time < row.dep_time;
      }

      row.valid = row.warnings.length === 0;
      updated[rowIndex] = row;
      return updated;
    });
  };

  const needsTimeEdit = (row: ValidatedFlightPatternRow, field: 'dep_time' | 'arr_time') => {
    const otherField = field === 'dep_time' ? 'arr_time' : 'dep_time';
    const thisOk = isValidTime(row[field]);
    const otherOk = isValidTime(row[otherField]);
    // Only show edit input if BOTH times are bad (row is invalid due to times)
    return !row.valid && !thisOk && !otherOk && (row[field] === '?' || row[field] === '');
  };

  // Classify each row
  const classifications = useMemo(() => {
    return parsedRows.map(row => classifyFlightRow(row.flight_number, flightPrefix, handledCodes));
  }, [parsedRows, flightPrefix, handledCodes]);

  const validRows = parsedRows.filter((r) => r.valid);
  const warningCount = parsedRows.filter((r) => !r.valid).length;

  // Rows that are both valid AND included
  const importableRows = parsedRows.filter((r, idx) => r.valid && includedRows.has(idx));
  const importableCount = importableRows.length;

  // Breakdown stats
  const stats = useMemo(() => {
    let ownTotal = 0, ownSelected = 0;
    let handledTotal = 0, handledSelected = 0;
    let unrecTotal = 0, unrecSelected = 0;
    parsedRows.forEach((_, idx) => {
      const cl = classifications[idx]?.classification;
      const selected = includedRows.has(idx);
      if (cl === 'own') { ownTotal++; if (selected) ownSelected++; }
      else if (cl === 'handled') { handledTotal++; if (selected) handledSelected++; }
      else { unrecTotal++; if (selected) unrecSelected++; }
    });
    return { ownTotal, ownSelected, handledTotal, handledSelected, unrecTotal, unrecSelected };
  }, [parsedRows, classifications, includedRows]);

  const toggleRow = useCallback((idx: number) => {
    setIncludedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const selectAllOwn = useCallback(() => {
    setIncludedRows(prev => {
      const next = new Set(prev);
      parsedRows.forEach((_, idx) => {
        if (classifications[idx]?.classification === 'own') next.add(idx);
      });
      return next;
    });
  }, [parsedRows, classifications]);

  const selectAll = useCallback(() => {
    setIncludedRows(new Set(parsedRows.map((_, idx) => idx)));
  }, [parsedRows]);

  const deselectAll = useCallback(() => {
    setIncludedRows(new Set());
  }, []);

  const removeRow = useCallback((idx: number) => {
    const row = parsedRows[idx];
    const entry: RemovedRowEntry = { originalIndex: idx, row };
    setLastRemoved(entry);
    setRemovedRows(prev => [...prev, entry]);
    setParsedRows(prev => prev.filter((_, i) => i !== idx));
    // Update includedRows: remove this index and shift higher indices down
    setIncludedRows(prev => {
      const next = new Set<number>();
      prev.forEach(i => {
        if (i < idx) next.add(i);
        else if (i > idx) next.add(i - 1);
        // i === idx is removed
      });
      return next;
    });
  }, [parsedRows]);

  const undoLastRemove = useCallback(() => {
    if (!lastRemoved) return;
    const { originalIndex, row } = lastRemoved;
    // Re-insert at original position (clamped to current length)
    const insertAt = Math.min(originalIndex, parsedRows.length);
    setParsedRows(prev => {
      const next = [...prev];
      next.splice(insertAt, 0, row);
      return next;
    });
    // Shift includedRows up for indices >= insertAt
    setIncludedRows(prev => {
      const next = new Set<number>();
      prev.forEach(i => {
        if (i >= insertAt) next.add(i + 1);
        else next.add(i);
      });
      return next;
    });
    setRemovedRows(prev => prev.slice(0, -1));
    setLastRemoved(null);
  }, [lastRemoved, parsedRows.length]);

  // Board stats
  const boardTodayCount = useMemo(() => {
    return parsedRows.filter(row => {
      const bs = getBoardStatus(row, scheduleType, scheduleDate, header);
      return bs.isToday;
    }).length;
  }, [parsedRows, scheduleType, scheduleDate, header]);

  // Unrecognised rows for the modal
  const unrecognisedRows = useMemo(() => {
    return parsedRows
      .map((row, idx) => ({ row, index: idx, cl: classifications[idx] }))
      .filter(item => item.cl?.classification === 'unrecognised')
      .map(item => ({ row: item.row, index: item.index }));
  }, [parsedRows, classifications]);

  const handleApplyFixed = useCallback((fixedRows: { row: ValidatedFlightPatternRow; originalIndex: number; classification: 'own' | 'handled'; handledName?: string }[]) => {
    // Update the rows in parsedRows with fixed data
    setParsedRows(prev => {
      const updated = [...prev];
      for (const fixed of fixedRows) {
        if (fixed.originalIndex < updated.length) {
          updated[fixed.originalIndex] = fixed.row;
        }
      }
      return updated;
    });
    // Auto-include fixed rows
    setIncludedRows(prev => {
      const next = new Set(prev);
      for (const fixed of fixedRows) {
        next.add(fixed.originalIndex);
      }
      return next;
    });
    toast({ title: "Rows updated", description: `${fixedRows.length} rows fixed and moved to import list.` });
  }, [toast]);

  const handleDeleteFromModal = useCallback((originalIndices: number[]) => {
    if (originalIndices.length === 0) return;
    // Sort descending to remove from end first
    const sorted = [...originalIndices].sort((a, b) => b - a);
    setParsedRows(prev => prev.filter((_, i) => !originalIndices.includes(i)));
    setIncludedRows(prev => {
      const removed = new Set(originalIndices);
      const next = new Set<number>();
      // Rebuild indices accounting for removed rows
      let shift = 0;
      for (let i = 0; i < parsedRows.length; i++) {
        if (removed.has(i)) { shift++; continue; }
        if (prev.has(i)) next.add(i - shift);
      }
      return next;
    });
    toast({ title: "Rows deleted", description: `${originalIndices.length} rows removed from import.` });
  }, [parsedRows.length, toast]);

  const handleHandledCodeRegistered = useCallback((code: string, name: string) => {
    setHandledCodes(prev => {
      const next = new Map(prev);
      next.set(code, name);
      return next;
    });
  }, []);

  const handleImport = async () => {
    console.log('handleImport guard check — parsedRows:', parsedRows.length, 'includedRows size:', includedRows.size, 'valid+included (importableCount):', importableCount, 'header:', !!header);
    if (importableCount === 0) return;

    // Use extracted header or build fallback from form fields
    const effectiveHeader: ScheduleHeader = header || {
      schedule_code: scheduleVersion.trim() || 'MANUAL',
      valid_from: format(scheduleDate, 'yyyy-MM-dd'),
      valid_to: format(scheduleDate, 'yyyy-MM-dd'),
    };
    console.log('handleImport: using header', effectiveHeader);
    setImporting(true);
    setImportProgress(null);

    try {
      console.log('handleImport: starting with', importableCount, 'rows');

      // Build handled flights info for logging
      const handledFlightsInfo: HandledFlightInfo[] = [];
      if (handledCodes.size > 0) {
        const countMap = new Map<string, { name: string; count: number }>();
        importableRows.forEach(row => {
          const cl = classifyFlightRow(row.flight_number, flightPrefix, handledCodes);
          if (cl.classification === 'handled' && cl.handledName) {
            const prefix = row.flight_number.replace(/[^A-Za-z]/g, '').toUpperCase();
            const existing = countMap.get(prefix);
            if (existing) existing.count++;
            else countMap.set(prefix, { name: cl.handledName, count: 1 });
          }
        });
        countMap.forEach((val, code) => {
          handledFlightsInfo.push({ handled_code: code, handled_name: val.name, count: val.count });
        });
      }

      console.log('handleImport: calling importSchedule');
      const result = await importSchedule(
        importableRows,
        effectiveHeader,
        airlineCode.trim(),
        scheduleVersion.trim(),
        (progress) => {
          console.log('Import progress:', progress.step, progress.message);
          setImportProgress(progress);
        },
        fileHash || undefined,
        scheduleType || 'seasonal',
        handledFlightsInfo.length > 0 ? handledFlightsInfo : undefined
      );

      setImporting(false);
      console.log('handleImport: result', result.step, result.message);

      if (result.step === 'done') {
        toast({
          title: "Import successful",
          description: result.message,
        });
        setLibraryRefreshKey(k => k + 1);
        setTimeout(() => {
          setFile(null);
          setFileHash(null);
          setDuplicateInfo(null);
          setScheduleVersion("");
          setScheduleType(null);
          setRawMarkdown(null);
          setError(null);
          setHeader(null);
          setParsedRows([]);
          setImportProgress(null);
          setResolvedCount(0);
          setHandledCodes(new Map());
          setIncludedRows(new Set());
          setRemovedRows([]);
          setLastRemoved(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }, 2000);
      } else {
        toast({
          title: "Import failed",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (err: unknown) {
      console.error('handleImport crashed:', err);
      setImporting(false);
      toast({
        title: "Import error",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: "destructive",
      });
    }
  };

  if (!authChecked) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-5xl flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-5xl space-y-6">
      {/* Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            NAC Schedule Import Tool
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pdf-file">PDF File</Label>
            <div className="flex items-center gap-2">
              <Input
                id="pdf-file"
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
              />
              {file && !checkingHash && (
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {file.name}
                </span>
              )}
              {checkingHash && (
                <span className="text-sm text-muted-foreground whitespace-nowrap flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Checking...
                </span>
              )}
            </div>
          </div>

          {/* Duplicate Warning */}
          {duplicateInfo && (
            <Alert className="border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold text-amber-900 dark:text-amber-200">
                    ⚠️ This PDF has already been imported
                  </p>
                  <p className="text-amber-800 dark:text-amber-300 text-sm">
                    Schedule: <strong>{duplicateInfo.schedule_version}</strong> for{' '}
                    <strong>{duplicateInfo.airline_code}</strong>
                    {duplicateInfo.imported_at && (
                      <> on {new Date(duplicateInfo.imported_at).toLocaleDateString()}</>
                    )}
                  </p>
                  <p className="text-amber-700 dark:text-amber-400 text-sm">
                    Do you want to continue anyway?
                  </p>
                  <div className="flex gap-2 mt-1">
                    <Button variant="outline" size="sm" onClick={handleDuplicateCancel}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={handleDuplicateContinue}
                    >
                      Import Anyway
                    </Button>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="airline-code">Airline Code</Label>
              {userRole === 'admin' ? (
                <Select
                  value={airlineCode}
                  onValueChange={(val) => {
                    setAirlineCode(val);
                    const org = organizations.find(o => o.code === val);
                    setSelectedOrgName(org?.name || null);
                  }}
                >
                  <SelectTrigger id="airline-code">
                    <SelectValue placeholder="Select airline" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.code}>
                        {org.code} — {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="relative">
                  <Input
                    id="airline-code"
                    value={airlineCode}
                    readOnly={airlineCodeLocked}
                    onChange={(e) => !airlineCodeLocked && setAirlineCode(e.target.value)}
                    placeholder="e.g. ANG"
                    className={airlineCodeLocked ? "pr-8 bg-muted cursor-not-allowed" : ""}
                  />
                  {airlineCodeLocked && (
                    <Lock className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              )}
              {selectedOrgName && (
                <p className="text-sm text-muted-foreground font-medium">{selectedOrgName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-version">Schedule Version</Label>
              <Input
                id="schedule-version"
                value={scheduleVersion}
                onChange={(e) => setScheduleVersion(e.target.value)}
                placeholder="e.g. SCH-141B"
              />
            </div>
          </div>

          {/* Schedule Type Selector */}
          <ScheduleTypeSelector value={scheduleType} onChange={setScheduleType} />

          {/* Chunk size for seasonal */}
          {scheduleType === 'seasonal' && (
            <div className="space-y-1">
              <Label htmlFor="chunk-size">Pages per chunk</Label>
              <Input
                id="chunk-size"
                type="number"
                min={1}
                max={10}
                value={chunkSize}
                onChange={(e) => setChunkSize(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                Lower values improve accuracy for complex multi-column schedules. Use 1 for Air Niugini seasonal timetables.
              </p>
            </div>
          )}

          {/* Daily schedule date picker */}
          {scheduleType === 'daily' && (
            <ScheduleDatePicker value={scheduleDate} onChange={setScheduleDate} />
          )}

          <Button onClick={handleParse} disabled={loading || !!duplicateInfo || !file || !scheduleType} className="w-full">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Parsing PDF...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Parse PDF
              </>
            )}
          </Button>

          {/* Progress indicator while parsing */}
          {loading && (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400 shrink-0" />
              <div>
                <p className="font-medium text-blue-900 dark:text-blue-100">Parsing PDF with AI</p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  This may take up to 5 minutes for large schedules. Please don't close this page.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-destructive font-medium">{error}</p>
                {isTimeoutError(error) && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Tip: If the PDF has many pages, the AI parser may need more time. Try again — it often succeeds on retry.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Header */}
      {header && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Valid From</Label>
                <p className="font-medium">{header.valid_from || '—'}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Valid To</Label>
                <p className="font-medium">{header.valid_to || '—'}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Schedule Code</Label>
                <p className="font-medium">{header.schedule_code || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Type Summary + Preview Table */}
      {parsedRows.length > 0 && (
        <>
          {/* Summary bar */}
          <div className="rounded-lg border px-4 py-3 text-sm font-medium" style={{ backgroundColor: '#0d1b4b', color: '#ffffff' }}>
            <div className="flex flex-col gap-1">
              <div>
                {scheduleType === 'daily' && header ? (
                  <>Daily operational schedule — {format(scheduleDate, 'EEEE, d MMMM yyyy')}</>
                ) : header ? (
                  <>Seasonal schedule — valid {header.valid_from} to {header.valid_to}</>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-3 text-xs opacity-90">
                <span>{parsedRows.length} total rows parsed</span>
                <span>✈️ Own: {stats.ownTotal} — {stats.ownSelected} selected</span>
                <span>🤝 Handled: {stats.handledTotal} — {stats.handledSelected} selected</span>
                <span>
                  ⚠️ Unrecognised: {stats.unrecTotal} — {stats.unrecSelected} selected
                  {stats.unrecTotal > 0 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-xs text-amber-300 hover:text-amber-100 ml-1 h-auto p-0 underline"
                      onClick={() => setReviewModalOpen(true)}
                    >
                      Review &amp; Fix →
                    </Button>
                  )}
                </span>
                <span>📺 Showing today: {boardTodayCount} flights</span>
                {removedRows.length > 0 && (
                  <span>Removed: {removedRows.length} rows</span>
                )}
              </div>
            </div>
          </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Parsed Flight Patterns</CardTitle>
            <div className="flex items-center gap-4 mt-2 flex-wrap">
              <Badge variant="secondary">{parsedRows.length} total rows</Badge>
              <Badge className="bg-green-600 text-white hover:bg-green-700">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {validRows.length} valid
              </Badge>
              {resolvedCount > 0 && (
                <Badge variant="outline" className="border-blue-500 text-blue-600 dark:text-blue-400">
                  🔧 {resolvedCount} times auto-resolved
                </Badge>
              )}
              {warningCount > 0 && (
                <Badge variant="destructive">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {warningCount} warnings
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button variant="outline" size="sm" onClick={selectAllOwn}>Select All Own</Button>
              <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>Deselect All</Button>
              {removedRows.length > 0 && lastRemoved && (
                <Button variant="outline" size="sm" onClick={undoLastRemove} className="ml-2">
                  Undo Last Remove
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <TooltipProvider>
            <div className="overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Flight</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Dep</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Arr</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Aircraft</TableHead>
                    <TableHead>Overnight</TableHead>
                    <TableHead>Board</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Include</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.map((row, idx) => {
                    const cl = classifications[idx];
                    const isIncluded = includedRows.has(idx);
                    return (
                    <TableRow
                      key={idx}
                      className={row.valid ? "bg-green-50 dark:bg-green-950/20" : "bg-red-50 dark:bg-red-950/20"}
                    >
                      <TableCell className="text-xs whitespace-nowrap">
                        {cl?.classification === 'own' && (
                          <span>✈️ Own</span>
                        )}
                        {cl?.classification === 'handled' && (
                          <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium" style={{ backgroundColor: '#F59E0B', color: '#1a1a1a' }}>
                            🤝 Handled — {cl.handledName}
                          </span>
                        )}
                        {cl?.classification === 'unrecognised' && (
                          <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium" style={{ backgroundColor: '#DC2626', color: '#ffffff' }}>
                            ⚠️ Unrecognised
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono font-medium">{row.flight_number}</TableCell>
                      <TableCell className="font-mono">{row.dep_airport}</TableCell>
                      <TableCell>
                        {needsTimeEdit(row, 'dep_time') ? (
                          <input
                            type="time"
                            value={row.dep_time === '?' ? '' : row.dep_time}
                            onChange={(e) => handleTimeChange(idx, 'dep_time', e.target.value)}
                            className="bg-amber-100 dark:bg-amber-900/40 border-2 border-amber-400 dark:border-amber-600 rounded px-2 py-1 text-sm w-24"
                          />
                        ) : (
                          row.dep_time && isValidTime(row.dep_time) ? row.dep_time : <span className="text-muted-foreground italic">TBA</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">{row.arr_airport}</TableCell>
                      <TableCell>
                        {needsTimeEdit(row, 'arr_time') ? (
                          <input
                            type="time"
                            value={row.arr_time === '?' ? '' : row.arr_time}
                            onChange={(e) => handleTimeChange(idx, 'arr_time', e.target.value)}
                            className="bg-amber-100 dark:bg-amber-900/40 border-2 border-amber-400 dark:border-amber-600 rounded px-2 py-1 text-sm w-24"
                          />
                        ) : (
                          row.arr_time && isValidTime(row.arr_time) ? row.arr_time : <span className="text-muted-foreground italic">TBA</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{row.days_display}</TableCell>
                      <TableCell className="text-xs">{row.aircraft_type}</TableCell>
                      <TableCell>{row.is_overnight ? "🌙" : "—"}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {(() => {
                          const bs = getBoardStatus(row, scheduleType, scheduleDate, header);
                          return bs.isToday
                            ? <span style={{ color: '#16A34A' }} className="font-medium">📺 Today</span>
                            : <span>{bs.label}</span>;
                        })()}
                      </TableCell>
                      <TableCell>
                        {row.valid ? (
                          <span className="flex items-center gap-1 text-green-700 dark:text-green-400 text-xs">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Valid
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-destructive text-xs" title={row.warnings.join('; ')}>
                            <AlertTriangle className="h-3.5 w-3.5" /> Check
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {cl?.classification === 'unrecognised' ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Checkbox
                                  checked={isIncluded}
                                  onCheckedChange={() => toggleRow(idx)}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>This flight prefix is not recognised. Verify before including.</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Checkbox
                            checked={isIncluded}
                            onCheckedChange={() => toggleRow(idx)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeRow(idx)}
                          title="Remove row"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            </TooltipProvider>

            {importProgress && (
              <div className={`mt-4 p-3 rounded-md text-sm font-medium ${
                importProgress.step === 'error' ? 'bg-destructive/10 text-destructive' :
                importProgress.step === 'done' ? 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400' :
                'bg-muted text-muted-foreground'
              }`}>
                {importProgress.message}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleImport}
                disabled={importableCount === 0 || importing}
                className="gap-2"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Import className="h-4 w-4" />
                    Confirm &amp; Import ({importableCount} rows)
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
        </>
      )}

      {/* Raw Markdown (collapsible) */}
      {rawMarkdown && (
        <details className="group">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
            Show raw markdown output
          </summary>
          <Card className="mt-2">
            <CardContent className="pt-4">
              <pre className="bg-muted p-4 rounded-md text-sm overflow-auto max-h-[400px] whitespace-pre-wrap">
                {rawMarkdown}
              </pre>
            </CardContent>
          </Card>
        </details>
      )}

      {/* Schedule Library */}
      <ScheduleLibrary refreshKey={libraryRefreshKey} />

      {/* Cache Status */}
      <CacheStatus refreshKey={libraryRefreshKey} />

      {/* Unrecognised Rows Modal */}
      <UnrecognisedRowsModal
        open={reviewModalOpen}
        onOpenChange={setReviewModalOpen}
        unrecognisedRows={unrecognisedRows}
        cityCodes={cityCodes}
        flightPrefix={flightPrefix}
        handledCodes={handledCodes}
        currentOrgId={organizations.find(o => o.code === airlineCode)?.id || ''}
        currentOrgName={selectedOrgName || airlineCode}
        onApplyFixed={handleApplyFixed}
        onDeleteRows={handleDeleteFromModal}
        onHandledCodeRegistered={handleHandledCodeRegistered}
      />
    </div>
  );
}
